import { useEffect, useMemo, useState } from 'react'

import { PlusOutlined } from '@ant-design/icons'
import {
  Flex,
  Modal,
  Space,
  Upload,
  type UploadFile,
  type UploadProps,
} from 'antd'
import CropperImage from 'antd-cropper-img'
import { useUnit } from 'effector-react'

import { createSingleUploadModel } from '../lib/upload-file-single-model'
import { type UploadFileWithId } from '../model'

type UploadPictureProps = Pick<UploadProps, 'id' | 'accept'> & {
  value?: UploadFileWithId
  onChange?: (fileList: UploadFileWithId[]) => void
  onReset?: () => void
  showRemoveIcon?: boolean
  showUploadButton?: boolean
}
export function UploadPicture({
  id,
  accept = 'image/jpeg,image/png',
  value,
  onChange,
  onReset,
  showRemoveIcon = true,
  showUploadButton = true,
}: UploadPictureProps) {
  const uploadModel = useMemo(() => createSingleUploadModel(), [])
  const [fileStore, addFile, removeFile, initFile] = useUnit([
    uploadModel.$file,
    uploadModel.fileAdded,
    uploadModel.fileRemoved,
    uploadModel.fileInit,
  ])
  const UploadModelGate = uploadModel.Gate

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')

  const handlePreview = (file: UploadFile) => {
    if (typeof file.url !== 'string') return
    const url = file.url
    setPreviewImage(url)
    setPreviewOpen(true)
    setPreviewTitle(file.name || url.substring(url.lastIndexOf('/') + 1))
  }

  useEffect(() => {
    if (fileStore.file !== value) {
      if (fileStore.file) {
        onChange?.([fileStore.file])
      } else if (value === null || value === undefined) {
        onChange?.([])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStore.file, value])

  useEffect(() => {
    if (value && fileStore.file === null) {
      initFile(value)
    } else if (!value && fileStore.file) {
      removeFile()
    }
  }, [value, initFile, removeFile, fileStore.file])

  return (
    <>
      <UploadModelGate />
      <Space direction="vertical">
        <CropperImage modalTitle="Загрузка изображения">
          <Upload
            id={id}
            pastable
            name="picture"
            listType="picture-card"
            accept={accept}
            maxCount={1}
            onPreview={handlePreview}
            fileList={value ? [value] : []}
            onChange={(e) => {
              onChange?.(e.fileList)
              return false
            }}
            beforeUpload={(file) => {
              addFile(file)
              return false
            }}
            onRemove={() => {
              removeFile()
              onReset?.()
              return false
            }}
            showUploadList={{
              showPreviewIcon: true,
              showRemoveIcon: showRemoveIcon,
            }}
          >
            {showUploadButton && !fileStore.file && (
              <button
                type="button"
                style={{
                  color: 'inherit',
                  cursor: 'pointer',
                  border: 0,
                  background: 'none',
                }}
              >
                <Flex vertical align="center">
                  <PlusOutlined />
                  <span style={{ marginTop: 8 }}>Загрузить</span>
                </Flex>
              </button>
            )}
          </Upload>
        </CropperImage>
      </Space>
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </>
  )
}
